/**
 * Car makes and their common models — the source both the booking form
 * and the shop's own intake screens suggest from.
 *
 * Shared because it was typed into `apps/web` and nowhere else, so every
 * management surface that asks for a vehicle offered a bare text box:
 * the New-job guest block, both New-vehicle modals, the inline add and
 * the vehicle form. A shop that types "bmw", "BMW" and "Bmw" into three
 * screens cannot group a fleet by make afterwards.
 *
 * Deliberately a SUGGESTION list, never a constraint. A workshop sees
 * marques no list of thirty predicts — kit cars, imports, a Piaggio Ape
 * — and refusing them would make the field unusable. Everything here
 * only offers.
 */

/**
 * Make and model are written in capitals everywhere (2026-10-29): the
 * API stores them uppercase, so every form shows them that way as they are
 * typed, and the suggestions below are offered the same way. Not trimmed —
 * this runs on every keystroke, and a space is how "LAND ROVER" is typed.
 */
export function normalizeMakeModel(raw: string): string {
  return raw.toUpperCase()
}

const BRANDS = [
  'Alfa Romeo',
  'Audi',
  'BMW',
  'Citroën',
  'Dacia',
  'Fiat',
  'Ford',
  'Honda',
  'Hyundai',
  'Jaguar',
  'Kia',
  'Land Rover',
  'Lexus',
  'Mazda',
  'Mercedes-Benz',
  'Mini',
  'Mitsubishi',
  'Nissan',
  'Opel',
  'Peugeot',
  'Porsche',
  'Renault',
  'Seat',
  'Skoda',
  'Smart',
  'Subaru',
  'Suzuki',
  'Tesla',
  'Toyota',
  'Volkswagen',
  'Volvo',
] as const

export const CAR_BRANDS: readonly string[] = BRANDS.map(normalizeMakeModel)

const MODELS: Readonly<Record<string, readonly string[]>> = {
  'BMW': ['1 Series', '2 Series', '3 Series', '5 Series', '7 Series', 'X1', 'X3', 'X5', 'X7', 'M3', 'M5', 'i4', 'iX'],
  'Audi': ['A1', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'Q3', 'Q5', 'Q7', 'Q8', 'RS6', 'e-tron', 'TT'],
  'Volkswagen': ['Polo', 'Golf', 'Passat', 'Tiguan', 'Touareg', 'T-Roc', 'T-Cross', 'ID.3', 'ID.4', 'ID.7'],
  'Mercedes-Benz': ['A-Class', 'C-Class', 'E-Class', 'S-Class', 'CLA', 'GLA', 'GLC', 'GLE', 'GLS', 'EQA', 'EQE', 'EQS'],
  'Skoda': ['Fabia', 'Scala', 'Octavia', 'Superb', 'Kamiq', 'Karoq', 'Kodiaq', 'Enyaq'],
  'Ford': ['Fiesta', 'Focus', 'Mondeo', 'Kuga', 'Puma', 'Mustang', 'Mustang Mach-E', 'Ranger'],
  'Opel': ['Corsa', 'Astra', 'Insignia', 'Mokka', 'Crossland', 'Grandland'],
  'Renault': ['Clio', 'Megane', 'Captur', 'Kadjar', 'Scenic', 'Talisman', 'Zoe'],
  'Toyota': ['Yaris', 'Corolla', 'C-HR', 'RAV4', 'Camry', 'Prius', 'Land Cruiser', 'Hilux'],
  'Hyundai': ['i10', 'i20', 'i30', 'Tucson', 'Kona', 'Santa Fe', 'IONIQ 5', 'IONIQ 6'],
  'Kia': ['Picanto', 'Rio', 'Ceed', 'Sportage', 'Sorento', 'Niro', 'EV6', 'EV9'],
  'Tesla': ['Model 3', 'Model Y', 'Model S', 'Model X'],
  'Porsche': ['911', '718', 'Cayenne', 'Macan', 'Panamera', 'Taycan'],
  'Volvo': ['XC40', 'XC60', 'XC90', 'V60', 'V90', 'S60', 'S90', 'EX30', 'EX90'],
}

/** Keyed and listed uppercase, like {@link CAR_BRANDS}. */
export const POPULAR_MODELS: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
  Object.entries(MODELS).map(([make, models]) => [
    normalizeMakeModel(make),
    models.map(normalizeMakeModel),
  ]),
)

/**
 * Models to suggest for a make, matched case-insensitively so a typed
 * "bmw" still finds BMW's list. Empty for a make nobody has curated —
 * the field stays free text.
 */
export function modelsForMake(make: string): readonly string[] {
  const key = make.trim().toLowerCase()
  if (!key) return []
  const hit = Object.keys(POPULAR_MODELS).find((k) => k.toLowerCase() === key)
  return hit ? POPULAR_MODELS[hit]! : []
}
